// src/hooks/useFormValidationGuard.ts

import { useEffect, useState, useCallback, useRef } from 'react';
import { formService } from '@/services/formService';

type ValidationResult = 'valid' | 'not-found' | 'network-error';

export const useFormValidationGuard = (formId?: string) => {
    const [isValidating, setIsValidating] = useState(true);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const isMountedRef = useRef(true);
    useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

    const validateForm = useCallback(async (id: string, signal?: AbortSignal) => {
        if (!id) return 'not-found' as ValidationResult;

        setIsValidating(true);
        try {
            await formService.getForm(id, signal);
            if (!isMountedRef.current) return 'not-found' as ValidationResult;
            setValidationResult('valid');
            return 'valid' as ValidationResult;
        } catch (error) {
            if (!isMountedRef.current) return 'not-found' as ValidationResult;
            // Clear cached validation so the guard re-checks on next visit
            sessionStorage.removeItem(`form_validated_${id}`);

            const err = error as { constraints?: Array<{ type?: string }>, code?: number, name?: string };
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return 'not-found' as ValidationResult;
            }

            // Any 4xx client error means the form is not available (not found, invalid, etc.)
            // Only 5xx or no-response errors are true network/server errors
            const isClientError = err.code !== undefined && err.code >= 400 && err.code < 500;
            const isNotFound = isClientError
                || err.constraints?.some(c => c.type === 'NOT_FOUND')
                || err.code === 404;
            const result: ValidationResult = isNotFound ? 'not-found' : 'network-error';
            setValidationResult(result);
            return result;
        } finally {
            if (isMountedRef.current) {
                setIsValidating(false);
            }
        }
    }, []);

    // Validate on mount or formId change
    useEffect(() => {
        if (!formId) {
            setIsValidating(false);
            setValidationResult('not-found');
            return;
        }

        // Check sessionStorage cache
        const hasValidated = sessionStorage.getItem(`form_validated_${formId}`);
        if (hasValidated === 'true') {
            setValidationResult('valid');
            setIsValidating(false);
            return;
        }

        // Abort any previous in-flight request
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        validateForm(formId, controller.signal).then(result => {
            if (result === 'valid') {
                sessionStorage.setItem(`form_validated_${formId}`, 'true');
            }
        });

        return () => {
            controller.abort();
        };
    }, [formId, validateForm]);

    return {
        isValidating,
        isValid: validationResult === 'valid',
        isNotFound: validationResult === 'not-found',
        isNetworkError: validationResult === 'network-error',
        validateForm
    };
};
